(function() {
  'use strict';

  const whenVisible = window.theme?.whenVisible || ((element, callback) => callback(element));

  if (customElements.get('package-plans-section')) return;

  class PackagePlansSection extends HTMLElement {
    constructor() {
      super();
      this._swiper = null;
      this._swiperPromise = null;
      this._activeConfigKey = null;
      this._resizeFrame = null;
      this._handleResize = this._handleResize.bind(this);
    }

    connectedCallback() {
      this._swiperElement = this.querySelector('.package-plans__swiper');
      this._navElement = this.querySelector('.package-plans__nav');

      if (!this._swiperElement) return;

      window.addEventListener('resize', this._handleResize, { passive: true });
      this._visibilityObserver = whenVisible(this, () => this._handleResize());
    }

    disconnectedCallback() {
      this._visibilityObserver?.disconnect?.();
      window.removeEventListener('resize', this._handleResize);

      if (this._resizeFrame) {
        cancelAnimationFrame(this._resizeFrame);
        this._resizeFrame = null;
      }

      this._destroySwiper();
    }

    _handleResize() {
      if (this._resizeFrame) cancelAnimationFrame(this._resizeFrame);

      this._resizeFrame = requestAnimationFrame(() => {
        this._resizeFrame = null;

        if (!this.isConnected || !this._swiperElement) return;

        const navigationVisible = this._shouldShowNavigation();
        this._updateControls(navigationVisible);

        if (!this._shouldEnableSlider() || !this._hasOverflow()) {
          this._destroySwiper();
          return;
        }

        if (this._swiper) {
          const config = this._getActiveConfig();
          const configKey = `${config.slidesPerView}:${config.spaceBetween}`;

          if (configKey === this._activeConfigKey) {
            this._swiper.update();
            return;
          }
        }

        this._ensureSwiper();
      });
    }

    _getActiveConfig() {
      const mobileBreakpoint = parseInt(this.dataset.mobileBreakpoint, 10) || 749;
      const tabletBreakpoint = parseInt(this.dataset.tabletBreakpoint, 10) || 1199;
      const width = window.innerWidth;

      if (width <= mobileBreakpoint) {
        if (this.dataset.enableMobileSlider !== 'true') return null;

        return {
          slidesPerView: parseFloat(this.dataset.slidesMobile) || 1,
          spaceBetween: parseInt(this.dataset.spaceBetweenMobile, 10) || 16
        };
      }

      if (this.dataset.enableDesktopSlider !== 'true') return null;

      const spaceBetween = parseInt(this.dataset.spaceBetweenDesktop, 10) || 24;

      if (width <= tabletBreakpoint) {
        return {
          slidesPerView: parseFloat(this.dataset.slidesTablet) || 2,
          spaceBetween: spaceBetween
        };
      }

      return {
        slidesPerView: parseFloat(this.dataset.slidesDesktop) || 3,
        spaceBetween: spaceBetween
      };
    }

    _shouldEnableSlider() {
      return this._getActiveConfig() !== null;
    }

    _getSlideCount() {
      return this._swiperElement?.querySelectorAll('.package-plans__slide').length || 0;
    }

    _getSlidesPerView() {
      return this._getActiveConfig()?.slidesPerView || 1;
    }

    _hasOverflow() {
      return this._getSlideCount() > Math.ceil(this._getSlidesPerView());
    }

    _shouldShowNavigation() {
      return this.dataset.showNavigation === 'true' && this._shouldEnableSlider() && this._hasOverflow();
    }

    _updateControls(navigationVisible) {
      if (this._navElement) {
        this._navElement.hidden = !navigationVisible;
      }
    }

    _ensureSwiper() {
      if (this._swiperPromise || typeof window.loadThemeSwiper !== 'function') return;

      if (this._swiper) {
        this._createSwiper();
        return;
      }

      this._swiperPromise = window.loadThemeSwiper()
        .then(() => {
          this._swiperPromise = null;

          if (!this.isConnected || !this._shouldEnableSlider() || !this._hasOverflow()) return;

          this._createSwiper();
        })
        .catch((error) => {
          this._swiperPromise = null;
          console.warn('Swiper failed to load for PackagePlans:', error);
        });
    }

    _createSwiper() {
      if (!this._shouldEnableSlider() || !this._hasOverflow()) return;

      this._destroySwiper();

      const config = this._getActiveConfig();
      const slidesPerView = config.slidesPerView;
      const spaceBetween = config.spaceBetween;
      const speed = parseInt(this.dataset.speed, 10) || 400;
      const loopEnabled = this.dataset.loop === 'true';
      const slideCount = this._getSlideCount();
      const loop = loopEnabled && slideCount > Math.ceil(slidesPerView);
      const navPrev = this.querySelector('.package-plans__nav-btn--prev');
      const navNext = this.querySelector('.package-plans__nav-btn--next');
      const showNavigation = this._shouldShowNavigation();
      const autoplay = this.dataset.autoplay === 'true'
        ? {
            delay: parseInt(this.dataset.autoplayDelay, 10) || 4000,
            disableOnInteraction: true,
            pauseOnMouseEnter: true
          }
        : false;

      this._swiper = new Swiper(this._swiperElement, {
        wrapperClass: 'package-plans__slides',
        slideClass: 'package-plans__slide',
        slidesPerView: slidesPerView,
        spaceBetween: spaceBetween,
        speed: speed,
        loop: loop,
        watchOverflow: true,
        grabCursor: true,
        navigation: showNavigation && navPrev && navNext
          ? {
              prevEl: navPrev,
              nextEl: navNext
            }
          : false,
        autoplay: autoplay
      });

      this._activeConfigKey = `${slidesPerView}:${spaceBetween}`;
    }

    _destroySwiper() {
      if (!this._swiper) return;

      this._swiper.destroy(true, true);
      this._swiper = null;
      this._activeConfigKey = null;
    }
  }

  customElements.define('package-plans-section', PackagePlansSection);
})();
